/**
 * ===========================================================================
 *  FLOATING DAMAGE NUMBERS  (world-space, pooled, zero per-frame allocation)
 * ===========================================================================
 *
 * `world.combat` calls `world.ui.damage(x, y, z, amount, kind)` once per
 * damaged body — during a musou sweep that is 15-25 calls in a single frame —
 * so this has to be free. It is:
 *
 *   - Fixed pool of POOL records, never grown, never garbage.
 *   - Number->string conversion is a lookup in a pre-built table (0..999), so
 *     even the text costs nothing at spawn time.
 *   - One canvas for the whole layer. Only the union of last frame's glyph
 *     boxes is cleared, so a 4K canvas is not refilled when three numbers are
 *     on screen.
 *   - When nothing is live the layer is skipped entirely and the canvas is
 *     left cleared.
 *
 * MOTION (reference plate section D: "small, fast, arcing upward, fading in
 * ~0.5 s"). A number is launched from the contact point with a real 3D
 * velocity and gravity, so it arcs in WORLD space and keeps its parallax as
 * the camera moves — a screen-space tween looks pasted on. Lateral velocity
 * comes from a private deterministic RNG instance (never Math.random, and
 * never the shared world.rng, which would shift every other system's stream).
 *
 * A rendered frame admits at most three numerals in normal play: two ordinary
 * values plus one higher-value event. One non-overlapping critical/officer/
 * kill/player exception may use a fourth slot. Admission is also enforced
 * across LIVE records (not just new calls in one tick), and the draw pass
 * rejects focal-core, duplicate and label/label overlap. This keeps successive
 * sweep frames from accumulating into a wall while retaining the event the
 * player needs.
 *
 * KINDS
 *   hit    small bone-white, the bread and butter
 *   crit   larger gold, wider outline, a spark tick (set by combat's opts.crit)
 *   kill   large ember with a KO flare
 *   block  small steel-blue "GUARD", no arc, drifts straight up
 *   player red, drawn bigger — damage taken reads differently from damage dealt
 */
import { RNG } from '../core/rng.js';

const POOL = 72;
/**
 * Normal presentation budget. One C4 into a packed ring calls `ui.damage()`
 * twenty-five times on the SAME frame; later hit windows can do it again before
 * the previous labels expire. The cap therefore applies to both new admissions
 * and all live/drawn records.
 */
const BASE_VISIBLE = 3;
const MAX_VISIBLE = 4;
const ORDINARY_VISIBLE = 2;
const COALESCE_R2 = 3.4 * 3.4;
const COALESCE_AGE = 0.10;
const COALESCE_COUNT = 5;
const COALESCE_VALUE = 999;
const LABEL_GAP = 10;
const SANS = '"Liberation Sans","Helvetica Neue",Helvetica,Arial,"DejaVu Sans",sans-serif';

/** Pre-built strings so fillText never has to allocate. */
const NUM = new Array(1000);
for (let i = 0; i < 1000; i++) NUM[i] = String(i);

/** Quantized canvas fonts avoid a template-string allocation on every draw. */
const FONT = new Array(73);
for (let i = 0; i < FONT.length; i++) FONT[i] = '700 ' + i + 'px ' + SANS;

const STYLE = {
  hit:    { size: 18, fill: '#f6efdd', edge: 'rgba(10,7,4,.92)', key: null, life: 0.36, rise: 3.4, glow: 0 },
  crit:   { size: 29, fill: '#ffd36a', edge: 'rgba(24,10,2,.95)', key: 'rgba(150,90,20,.9)', life: 0.60, rise: 4.1, glow: 10 },
  kill:   { size: 27, fill: '#ff9a48', edge: 'rgba(26,6,2,.95)', key: 'rgba(140,44,10,.9)', life: 0.46, rise: 3.9, glow: 11 },
  block:  { size: 16, fill: '#bcd4ea', edge: 'rgba(6,10,16,.9)', key: null, life: 0.42, rise: 2.4, glow: 0 },
  player: { size: 25, fill: '#ff6a55', edge: 'rgba(28,3,2,.95)', key: 'rgba(150,30,16,.9)', life: 0.62, rise: 3.7, glow: 9 },
};

const PRIORITY = { hit: 1, kill: 3, block: 3, crit: 4, player: 5 };
const IMPORTANT_META = Object.freeze({ important: true });

export function createDamageLayer() {
  const rng = new RNG(0x5b1d0a17);
  const pool = new Array(POOL);
  for (let i = 0; i < POOL; i++) {
    pool[i] = { on: 0, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, t: 0, life: 1,
                txt: '0', amount: 0, count: 1, priority: 1, aggregateClass: 0,
                ordinary: 1, seq: 0, kind: 'hit', scale: 1,
                sx: 0, sy: 0, size: 0, alpha: 0, phase: 0, near: 1,
                halfW: 0, halfH: 0, vis: 0, spin: 0 };
  }
  let head = 0, live = 0, sequence = 0, spawnedFrame = 0;
  let coalescedFrame = 0, droppedFrame = 0;
  const frameSlots = new Int16Array(MAX_VISIBLE);
  const framePriority = new Uint8Array(MAX_VISIBLE);
  const candidateSlots = new Int16Array(POOL);
  const acceptedSlots = new Int16Array(MAX_VISIBLE);
  let frameN = 0, frameOrdinary = 0;
  const stats = {
    // `live` is the number of records currently on screen. It is tracked in
    // the layer already; publishing it is what lets a critic score the
    // damage-number row at all (the hitfeel probe read -1 for 600 frames).
    live: 0,
    spawned: 0, coalesced: 0, dropped: 0, replaced: 0,
    lastSpawned: 0, lastVisible: 0, lastCoalesced: 0, lastDropped: 0,
    suppressedOverlap: 0, suppressedDuplicate: 0, suppressedHero: 0, suppressedBudget: 0,
    lastAdmitted: 0, lastOrdinary: 0, lastExceptional: 0,
    lastSuppressedOverlap: 0, lastSuppressedDuplicate: 0, lastSuppressedHero: 0, lastSuppressedBudget: 0,
    lastCriticalCandidates: 0, lastCriticalVisible: 0, lastAggregates: 0,
    peakVisible: 0, peakOrdinary: 0,
  };
  /** The most recently spawned record, so combat's bus 'hit' can upgrade it. */
  let last = null;
  // A dropped normal can immediately be upgraded by the following `hit` bus
  // callback (player/crit). Preserve its scalar arguments without allocating.
  let pending = false, pendingX = 0, pendingY = 0, pendingZ = 0, pendingAmount = 0;

  // dirty rect of the previous frame, in CSS px
  let d0x = 0, d0y = 0, d1x = 0, d1y = 0, dirty = false;

  function textFor(n) { return n >= 0 && n < 1000 ? NUM[n] : '999+'; }

  function frameIndex(slot) {
    for (let i = 0; i < frameN; i++) if (frameSlots[i] === slot) return i;
    return -1;
  }

  function fillRecord(r, x, y, z, amount, kind, priority, ordinary, aggregateClass) {
    if (!r.on) { live++; stats.live = live; }
    r.on = 1; r.t = 0;
    r.x = x; r.y = y; r.z = z;
    const s = STYLE[kind] || STYLE.hit;
    r.kind = STYLE[kind] ? kind : 'hit';
    r.priority = priority;
    r.ordinary = ordinary ? 1 : 0;
    r.aggregateClass = aggregateClass;
    r.seq = sequence++;
    r.life = s.life;
    const a = rng.next() * Math.PI * 2, sp = 0.55 + rng.next() * 1.15;
    r.vx = Math.cos(a) * sp;
    r.vz = Math.sin(a) * sp;
    r.vy = s.rise * (0.86 + rng.next() * 0.28);
    r.spin = (rng.next() - 0.5) * 0.16;
    r.count = 1;
    if (typeof amount === 'string') { r.amount = -1; r.txt = amount; r.scale = 1; }
    else {
      const n = Math.max(0, amount | 0);
      r.amount = n;
      r.txt = textFor(n);
      // bigger hits read bigger — DW scales the glyph with the blow
      r.scale = 0.86 + Math.min(1.0, n / 150) * 0.42;
    }
    r.vis = 0;
    last = r;
    return r;
  }

  function spawn(x, y, z, amount, kind, meta) {
    const nested = meta && meta.opts;
    const isPlayer = !!(meta && meta.player);
    const isOfficer = !!(meta && meta.officer);
    const isCritical = !!(meta && (meta.crit || meta.guardBreak ||
      (nested && (nested.crit || nested.guardBreak))));
    let resolved = STYLE[kind] ? kind : 'hit';
    // Combat's bus used to apply these one callback later. Resolving the
    // synchronous metadata here lets admission protect them before the cap.
    if (isPlayer) resolved = 'player';
    else if (isCritical) resolved = 'crit';
    let priority = PRIORITY[resolved] || 1;
    if (meta && Number.isFinite(meta.importance)) priority = Math.max(priority, meta.importance | 0);
    if (meta && (isPlayer || isOfficer || meta.important)) priority = Math.max(priority, isPlayer ? 5 : 4);
    if (isCritical) priority = Math.max(priority, 4);

    const ordinary = resolved === 'hit' && priority <= 1;
    const aggregateClass = ordinary ? 1 : (resolved === 'kill' && !isOfficer ? 2 :
      (resolved === 'crit' && !isOfficer ? 3 : 0));

    // Same-sweep values become a bounded truthful total. Five 176s read as
    // 880; a sixth is suppressed instead of turning the focal area into 999+.
    if (aggregateClass && typeof amount !== 'string') {
      const n = Math.max(0, amount | 0);
      let best = -1, bestD2 = COALESCE_R2;
      for (let i = 0; i < POOL; i++) {
        const r = pool[i];
        if (!r.on || r.aggregateClass !== aggregateClass || r.t > COALESCE_AGE ||
            r.count >= COALESCE_COUNT || r.amount < 0 || r.amount + n > COALESCE_VALUE) continue;
        const dx = r.x - x, dy = r.y - y, dz = r.z - z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 <= bestD2) { best = i; bestD2 = d2; }
      }
      if (best >= 0) {
        const r = pool[best], count = r.count + 1;
        r.amount += n;
        r.txt = textFor(r.amount);
        r.x += (x - r.x) / count; r.y += (y - r.y) / count; r.z += (z - r.z) / count;
        r.count = count;
        r.scale = 0.88 + Math.min(1, r.amount / 210) * 0.32;
        last = r; pending = false;
        coalescedFrame++; stats.coalesced++;
        return r;
      }
    }

    let liveOrdinary = 0;
    for (let i = 0; i < POOL; i++) if (pool[i].on && pool[i].ordinary) liveOrdinary++;
    const frameCapacity = frameN < BASE_VISIBLE || (frameN < MAX_VISIBLE && priority >= 3);
    const liveCapacity = live < BASE_VISIBLE || (live < MAX_VISIBLE && priority >= 3);
    const ordinaryCapacity = !ordinary || (frameOrdinary < ORDINARY_VISIBLE && liveOrdinary < ORDINARY_VISIBLE);

    let slot = -1;
    if (frameCapacity && liveCapacity && ordinaryCapacity) {
      for (let k = 0; k < POOL; k++) {
        const i = (head + k) % POOL;
        if (!pool[i].on) { slot = i; head = (i + 1) % POOL; break; }
      }
    } else {
      // A more important late event may replace the lowest-priority label.
      // When the current-frame budget is the blocker, replacement must come
      // from that frame so total admissions cannot silently grow past four.
      let lowPriority = 255, lowSeq = -1;
      for (let i = 0; i < POOL; i++) {
        const r = pool[i];
        if (!r.on || (!frameCapacity && frameIndex(i) < 0)) continue;
        if (!ordinaryCapacity && ordinary && !r.ordinary) continue;
        if (r.priority < lowPriority || (r.priority === lowPriority && r.seq > lowSeq)) {
          slot = i; lowPriority = r.priority; lowSeq = r.seq;
        }
      }
      if (slot < 0 || priority <= pool[slot].priority) slot = -1;
    }

    if (slot < 0) {
      last = null;
      pending = true; pendingX = x; pendingY = y; pendingZ = z; pendingAmount = amount;
      droppedFrame++; stats.dropped++;
      return null;
    }

    const fi = frameIndex(slot);
    if (pool[slot].on) {
      if (fi >= 0 && pool[slot].ordinary) frameOrdinary--;
      stats.replaced++;
    }
    if (fi >= 0) {
      framePriority[fi] = priority;
    } else {
      frameSlots[frameN] = slot;
      framePriority[frameN] = priority;
      frameN++;
    }
    if (ordinary) frameOrdinary++;
    pending = false;
    spawnedFrame++; stats.spawned++;
    return fillRecord(pool[slot], x, y, z, amount, resolved, priority, ordinary, aggregateClass);
  }

  /** Upgrade the last spawned number (combat emits 'hit' right after damage()). */
  function upgrade(kind) {
    if (!(kind in STYLE)) return;
    if ((!last || !last.on || last.t > 0) && pending) {
      pending = false;
      spawn(pendingX, pendingY, pendingZ, pendingAmount, kind, IMPORTANT_META);
      return;
    }
    if (!last || !last.on || last.t > 0) return;
    const wasOrdinary = last.ordinary;
    last.kind = kind;
    last.priority = PRIORITY[kind] || last.priority;
    last.ordinary = last.kind === 'hit' && last.priority <= 1 ? 1 : 0;
    last.aggregateClass = last.kind === 'crit' ? 3 : (last.kind === 'kill' ? 2 : (last.ordinary ? 1 : 0));
    for (let i = 0; i < frameN; i++) if (pool[frameSlots[i]] === last) {
      framePriority[i] = last.priority;
      if (wasOrdinary && !last.ordinary) frameOrdinary--;
      break;
    }
    last.life = STYLE[kind].life;
    last.vy = STYLE[kind].rise * (0.9 + 0.2 * ((last.vy * 7) % 1));
  }

  /**
   * Advance and draw. `ctx` is already fitted to CSS px at `dpr`.
   * Returns the number of live records.
   */
  function draw(ctx, w, h, cam, dt, THREE, scratch) {
    stats.lastSpawned = spawnedFrame;
    stats.lastAdmitted = frameN;
    stats.lastCoalesced = coalescedFrame;
    stats.lastDropped = droppedFrame;
    stats.lastVisible = 0;
    stats.lastOrdinary = 0;
    stats.lastExceptional = 0;
    stats.lastSuppressedOverlap = 0;
    stats.lastSuppressedDuplicate = 0;
    stats.lastSuppressedHero = 0;
    stats.lastSuppressedBudget = 0;
    stats.lastCriticalCandidates = 0;
    stats.lastCriticalVisible = 0;
    stats.lastAggregates = 0;
    spawnedFrame = 0;
    coalescedFrame = 0; droppedFrame = 0; frameN = 0; frameOrdinary = 0; pending = false;
    // clear only what we drew last frame
    if (dirty) {
      ctx.clearRect(d0x - 2, d0y - 2, (d1x - d0x) + 4, (d1y - d0y) + 4);
      dirty = false;
    }
    if (!live) return 0;

    let n0x = 1e9, n0y = 1e9, n1x = -1e9, n1y = -1e9, any = 0, candidates = 0;
    const hw = w * 0.5, hh = h * 0.5;

    // Pass one advances every live arc and caches stable projected bounds.
    // The candidate list is fixed storage; no array/object/text allocation.
    for (let i = 0; i < POOL; i++) {
      const r = pool[i];
      if (!r.on) continue;
      r.vis = 0;
      r.t += dt;
      if (r.t >= r.life) { r.on = 0; live--; stats.live = live; continue; }

      // world-space arc
      r.x += r.vx * dt; r.z += r.vz * dt;
      r.y += r.vy * dt;
      r.vy -= 9.4 * dt;
      r.vx -= r.vx * 2.6 * dt; r.vz -= r.vz * 2.6 * dt;

      const v = scratch;
      v.set(r.x, r.y, r.z);
      v.applyMatrix4(cam.matrixWorldInverse);
      if (v.z > -0.4) continue;                   // behind the lens
      const dist = -v.z;
      v.applyMatrix4(cam.projectionMatrix);
      const sx = hw + v.x * hw, sy = hh - v.y * hh;
      if (sx < -160 || sx > w + 160 || sy < -120 || sy > h + 120) continue;

      const S = STYLE[r.kind];
      const k = r.t / r.life;
      // pop in over the first 8 %, hold, fade the last 32 %
      const pop = k < 0.08 ? 0.55 + 0.45 * (k / 0.08) : 1;
      const fade = k > 0.68 ? 1 - (k - 0.68) / 0.32 : 1;
      const near = dist < 34 ? Math.min(1.5, 26 / Math.max(4, dist)) : 0.74;
      const rawSize = S.size * r.scale * pop * near;
      if (rawSize < 6.5 || fade <= 0.01) continue;
      const size = Math.max(7, Math.min(72, Math.round(rawSize)));
      r.sx = sx; r.sy = sy; r.size = size; r.alpha = fade;
      r.phase = k; r.near = near;
      r.halfW = size * (0.25 * r.txt.length + 0.22);
      r.halfH = size * 0.72;
      candidateSlots[candidates++] = i;
      if (r.kind === 'crit') stats.lastCriticalCandidates++;
    }

    // Importance first, then original admission order. Stable ordering keeps
    // the same projected label winning while it lives instead of flickering
    // between equal-value bodies on adjacent frames.
    for (let i = 1; i < candidates; i++) {
      const slot = candidateSlots[i], r = pool[slot];
      let j = i - 1;
      while (j >= 0) {
        const q = pool[candidateSlots[j]];
        if (q.priority > r.priority || (q.priority === r.priority && q.seq < r.seq)) break;
        candidateSlots[j + 1] = candidateSlots[j];
        j--;
      }
      candidateSlots[j + 1] = slot;
    }

    // Pass two admits only labels that preserve the combat focal area and do
    // not collide with a higher-priority label already accepted this frame.
    let accepted = 0, acceptedOrdinary = 0, extraUsed = 0;
    const heroX0 = w * 0.45, heroX1 = w * 0.55;
    const heroY0 = h * 0.40, heroY1 = h * 0.68;
    for (let i = 0; i < candidates; i++) {
      const slot = candidateSlots[i], r = pool[slot];
      const x0 = r.sx - r.halfW, x1 = r.sx + r.halfW;
      const y0 = r.sy - r.halfH, y1 = r.sy + r.halfH;

      if (x1 >= heroX0 && x0 <= heroX1 && y1 >= heroY0 && y0 <= heroY1) {
        stats.lastSuppressedHero++; stats.suppressedHero++;
        continue;
      }
      if ((r.ordinary && acceptedOrdinary >= ORDINARY_VISIBLE) || accepted >= MAX_VISIBLE ||
          (accepted >= BASE_VISIBLE && (r.priority < 3 || extraUsed))) {
        stats.lastSuppressedBudget++; stats.suppressedBudget++;
        continue;
      }

      let duplicate = false, overlap = false;
      for (let j = 0; j < accepted; j++) {
        const a = pool[acceptedSlots[j]];
        const dx = r.sx - a.sx, dy = r.sy - a.sy;
        // A repeated identical value in one rendered frame adds no information
        // after aggregation; retain the stable, earlier projection only.
        if (r.kind === a.kind && r.txt === a.txt) {
          duplicate = true; break;
        }
        if (Math.abs(dx) < r.halfW + a.halfW + LABEL_GAP &&
            Math.abs(dy) < r.halfH + a.halfH + LABEL_GAP) overlap = true;
      }
      if (duplicate) {
        stats.lastSuppressedDuplicate++; stats.suppressedDuplicate++;
        continue;
      }
      if (overlap) {
        stats.lastSuppressedOverlap++; stats.suppressedOverlap++;
        continue;
      }

      if (accepted >= BASE_VISIBLE) extraUsed = 1;
      acceptedSlots[accepted++] = slot;
      acceptedOrdinary += r.ordinary;
      r.vis = 1;
      if (r.kind === 'crit') stats.lastCriticalVisible++;
      if (r.count > 1) stats.lastAggregates++;
    }

    stats.lastVisible = accepted;
    stats.lastOrdinary = acceptedOrdinary;
    stats.lastExceptional = accepted - acceptedOrdinary;
    if (accepted > stats.peakVisible) stats.peakVisible = accepted;
    if (acceptedOrdinary > stats.peakOrdinary) stats.peakOrdinary = acceptedOrdinary;

    // Pass three draws only the accepted labels. Their screen positions still
    // come directly from world projection; suppression never nudges or jitters
    // a label to a misleading contact point.
    for (let i = 0; i < accepted; i++) {
      const r = pool[acceptedSlots[i]], S = STYLE[r.kind];
      const sx = r.sx, sy = r.sy, size = r.size;
      const k = r.phase, fade = r.alpha, near = r.near;

      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(sx, sy);
      if (r.spin) ctx.rotate(r.spin * (1 - k));
      ctx.font = FONT[size];
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      ctx.transform(0.9, 0, -0.10, 1, 0, 0);

      if (S.glow) { ctx.shadowColor = S.fill; ctx.shadowBlur = S.glow * near; }
      ctx.strokeStyle = S.edge;
      ctx.lineWidth = size * 0.20;
      ctx.strokeText(r.txt, 0, 0);
      if (S.key) { ctx.strokeStyle = S.key; ctx.lineWidth = size * 0.07; ctx.strokeText(r.txt, 0, 0); }
      ctx.shadowBlur = 0;

      // Solid procedural styling keeps this hot loop allocation-free. The
      // outline, key stroke and short glow already provide value separation.
      ctx.fillStyle = S.fill;
      ctx.fillText(r.txt, 0, 0);
      ctx.restore();

      // a KO gets a short flare tick behind the numeral
      if (r.kind === 'kill' && k < 0.4) {
        const f = 1 - k / 0.4;
        ctx.save();
        ctx.globalAlpha = fade * f * 0.7;
        ctx.strokeStyle = '#ffca7a';
        ctx.lineWidth = Math.max(1, size * 0.07);
        const rr = size * (0.75 + (1 - f) * 1.5);
        for (let a = 0; a < 4; a++) {
          const ang = a * Math.PI / 2 + 0.4;
          ctx.beginPath();
          ctx.moveTo(sx + Math.cos(ang) * rr * 0.55, sy + Math.sin(ang) * rr * 0.55);
          ctx.lineTo(sx + Math.cos(ang) * rr, sy + Math.sin(ang) * rr);
          ctx.stroke();
        }
        ctx.restore();
      }

      const pad = size * 2.4;
      if (sx - pad < n0x) n0x = sx - pad;
      if (sy - pad < n0y) n0y = sy - pad;
      if (sx + pad > n1x) n1x = sx + pad;
      if (sy + pad > n1y) n1y = sy + pad;
      any = 1;
    }

    if (any) {
      d0x = n0x < 0 ? 0 : n0x; d0y = n0y < 0 ? 0 : n0y;
      d1x = n1x > w ? w : n1x; d1y = n1y > h ? h : n1y;
      dirty = true;
    }
    return live;
  }

  function clear() {
    for (let i = 0; i < POOL; i++) pool[i].on = 0;
    live = 0; stats.live = 0; last = null; pending = false; frameN = 0; frameOrdinary = 0;
    spawnedFrame = 0; coalescedFrame = 0; droppedFrame = 0; dirty = true;
  }

  return { spawn, upgrade, draw, clear, stats, get live() { return live; }, pool: POOL };
}
